/**
 * PaletteExtensions
 *
 * Lets routes plug extra sections (workflow-level Actions, Ask-AI prompts,
 * and a free-form AI submit handler) into the single GlobalNavPalette
 * instance mounted by RootLayout, without coupling the palette to any
 * particular route's data.
 *
 * Design
 * ─ The palette mounts once. By default it shows nav-only rows.
 * ─ Any route that wants extras renders `<PaletteExtensions.Register />`
 *   in its tree; the component publishes its props into context on mount
 *   and clears them on unmount. The palette renders whatever it sees.
 * ─ The provider also exposes `isOpen` so siblings (e.g. the workflow
 *   designer's Esc handler) can branch on palette state without holding
 *   a second `useCommandPalette` instance.
 *
 * Why context instead of props on GlobalNavPalette?
 * ─ One mount, one ⌘K binding. Two mount sites = two `useCommandPalette`
 *   listeners + route gating logic, which is exactly what we're cleaning
 *   up here.
 * ─ Extras are scoped to the route's lifetime; useEffect cleanup gives us
 *   automatic reset on navigation, no manual book-keeping in callers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useCommandPalette } from '@/hooks/useCommandPalette';

/**
 * A workflow-level action surfaced in the palette's Actions section
 * (e.g. "Run workflow now", "New workflow"). Optional shortcut renders
 * as a keycap on the right.
 */
export interface PaletteAction {
  id: string;
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  execute: () => void;
}

/**
 * A canned AI prompt surfaced in the palette's Ask AI section. Selecting
 * one calls the registered `onAskSubmit` with `prompt`.
 */
export interface PaletteQuickAction {
  id: string;
  label: string;
  prompt: string;
}

/**
 * Bundle of AI-related extras. When present, the palette shows the
 * Ask AI section, exposes a send button, and lets the user submit the
 * free-form query as an AI prompt (Enter on the empty-results state).
 */
export interface PaletteAskExtension {
  /** Canned prompts to render as rows. */
  items: PaletteQuickAction[];
  /**
   * Suffix appended to the section header — e.g. "about this workflow"
   * or "about your selection". Falls back to a generic label when omitted.
   */
  subtitle?: string;
}

export interface PaletteExtensions {
  actions?: PaletteAction[];
  askAi?: PaletteAskExtension;
  /**
   * Free-form prompt submit. Called with the input value when the user
   * presses Enter on an empty result list (or clicks the send button)
   * with `askAi` registered.
   */
  onAskSubmit?: (prompt: string) => void;
}

interface PaletteContextValue {
  extensions: PaletteExtensions;
  setExtensions: (next: PaletteExtensions) => number;
  clearExtensions: (token: number) => void;
  // Palette open state is owned here (single useCommandPalette instance
  // in the provider). Both the GlobalNavPalette component and any sibling
  // (e.g. the designer's Esc handler) subscribe to the same source.
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const PaletteContext = createContext<PaletteContextValue | null>(null);

/** Stable empty default so consumers can rely on `extensions.actions ?? []`. */
const EMPTY_EXTENSIONS: PaletteExtensions = Object.freeze({});

interface PaletteExtensionsProviderProps {
  children: ReactNode;
}

export const PaletteExtensionsProvider = ({ children }: PaletteExtensionsProviderProps) => {
  const [extensions, setExtensionsState] = useState<PaletteExtensions>(EMPTY_EXTENSIONS);

  // The ONE useCommandPalette instance for the app. Owns the ⌘K listener
  // and the open boolean. The palette component and any other consumer
  // read this through context — no mirror effect, no double bindings.
  const { isOpen, open, close, toggle } = useCommandPalette();

  // Last-writer-wins via monotonic token. Multiple Register components
  // mounting in the same tree shouldn't normally happen, but if it does
  // we want the most recent setter to own the slot — and clear() must
  // only succeed if it matches the token it stamped on mount, so a stale
  // unmount doesn't wipe a newer registration.
  const tokenRef = useRef(0);

  const setExtensions = useCallback((next: PaletteExtensions) => {
    const token = (tokenRef.current += 1);
    setExtensionsState(next);
    return token;
  }, []);

  const clearExtensions = useCallback((token: number) => {
    if (token === tokenRef.current) {
      setExtensionsState(EMPTY_EXTENSIONS);
    }
  }, []);

  const value = useMemo<PaletteContextValue>(
    () => ({ extensions, setExtensions, clearExtensions, isOpen, open, close, toggle }),
    [extensions, setExtensions, clearExtensions, isOpen, open, close, toggle],
  );

  return <PaletteContext.Provider value={value}>{children}</PaletteContext.Provider>;
};

function useRequiredContext(): PaletteContextValue {
  const ctx = useContext(PaletteContext);
  if (!ctx) {
    throw new Error(
      'PaletteExtensions hooks must be used within <PaletteExtensionsProvider>',
    );
  }
  return ctx;
}

/** Read-only access to the registered extensions. Used by the palette. */
export function usePaletteExtensions(): PaletteExtensions {
  return useRequiredContext().extensions;
}

/**
 * Whether the palette is currently open. Used by route components that
 * need to coordinate (e.g. the designer's Esc handler defers to the
 * palette when it owns Escape).
 */
export function usePaletteIsOpen(): boolean {
  return useRequiredContext().isOpen;
}

/**
 * Palette open/close handle. Used by the GlobalNavPalette component
 * itself to render and close, and by any consumer that wants to drive
 * the palette imperatively. Shares the single state instance owned by
 * the provider — no second useCommandPalette.
 */
export function usePaletteHandle(): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} {
  const { isOpen, open, close, toggle } = useRequiredContext();
  return { isOpen, open, close, toggle };
}

interface RegisterProps extends PaletteExtensions {
  /**
   * Re-publish when any of these change. Mirrors useEffect deps semantics.
   * Defaults to a stable empty array — i.e. the first set of values wins
   * for the lifetime of the component. Pass changing deps when your
   * actions or quickActions are derived from state that updates.
   */
  resetOnDeps?: ReadonlyArray<unknown>;
}

/**
 * Registers extras with the palette for the lifetime of the rendering tree.
 *
 * ```tsx
 * <PaletteExtensions.Register
 *   actions={paletteActions}
 *   askAi={{ items: quickActions, subtitle, isStreaming }}
 *   onAskSubmit={handleSubmit}
 *   resetOnDeps={[paletteActions, quickActions, subtitle, isStreaming]}
 * />
 * ```
 *
 * The component renders nothing — it's a side-effect-only mount marker.
 * Multiple Register components in the same provider use last-writer-wins
 * and a token-guarded clear so stale unmounts don't blow away a newer
 * registration.
 *
 * **Stability note:** prop values themselves don't need to be referentially
 * stable — `latestRef` reads them fresh each render. But anything you put
 * in `resetOnDeps` does need stable identity, otherwise the effect will
 * republish on every render. Memoize object/array literals (e.g. wrap
 * `askAi` in `useMemo`) before referencing them in `resetOnDeps`.
 */
function RegisterComponent({
  actions,
  askAi,
  onAskSubmit,
  resetOnDeps,
}: RegisterProps): null {
  const { setExtensions, clearExtensions } = useRequiredContext();

  // Latest props read inside the publish effect without making them deps —
  // `resetOnDeps` is the explicit subscription contract. Conditional
  // spread keeps the shape compatible with exactOptionalPropertyTypes
  // and lets an undefined-on-re-render value cleanly drop the key.
  const latestRef = useRef<PaletteExtensions>({});
  latestRef.current = {
    ...(actions !== undefined && { actions }),
    ...(askAi !== undefined && { askAi }),
    ...(onAskSubmit !== undefined && { onAskSubmit }),
  };

  // Single token for this Register's lifetime, stamped on first publish
  // and re-stamped each republish. The unmount effect uses the latest
  // value so it always cancels the most recent publication.
  const mountedTokenRef = useRef<number | null>(null);

  // Re-publish whenever resetOnDeps changes. We don't clear-and-set here
  // (that would push the consumer through an empty intermediate state
  // and double the renders); setExtensions just stamps a fresh token
  // and overwrites the slot.
  useEffect(() => {
    mountedTokenRef.current = setExtensions(latestRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetOnDeps ?? []);

  // Clear only on real unmount. Uses the latest token, so the token
  // guard in clearExtensions still protects against races with a newer
  // Register that has taken over the slot.
  useEffect(() => {
    return () => {
      const token = mountedTokenRef.current;
      if (token != null) clearExtensions(token);
    };
  }, [clearExtensions]);

  return null;
}

/**
 * Namespace export so callers can write `<PaletteExtensions.Register/>`
 * (reads naturally) without colliding with the `PaletteExtensions` type.
 */
export const PaletteExtensions = {
  Register: RegisterComponent,
};
